import { Tag } from "lucide-react"

import { cn } from "@/lib/utils"

interface AstroDiscountBannerProps {
  /** Slide the banner up out of view — kept in sync with the bottom "Show me where
   * I belong" CTA, so both clear the screen once the price block is reached. */
  hidden?: boolean
}

/**
 * "85% Discount Activated" banner that floats over the top of the Astro Map
 * paywall — the one-time discount the user just unlocked. Figma-exact: a
 * `dark-surface` card framed by a 2px peach→purple-accent gradient border (pink at
 * the top fading to violet at the foot), a 20px `main-purple` glow and a gradient
 * tag badge. Fixed over the sticky header; slides up out of view together with the
 * bottom CTA once the user scrolls down to the price block.
 */
export function AstroDiscountBanner({ hidden = false }: AstroDiscountBannerProps) {
  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 z-40 transition-opacity duration-700",
        hidden && "opacity-0",
      )}
    >
      <div className="mx-auto w-full max-w-app px-4 pt-4 pb-2">
        {/* 2px gradient frame: the p-[2px] gap shows the peach→purple-accent gradient
            as the border; the inner card's 14px radius keeps the corners concentric. */}
        <div className="pointer-events-auto rounded-[16px] bg-gradient-to-b from-peach to-purple-accent p-[2px] shadow-[0_0_20px_0_var(--main-purple)]">
          <div className="flex items-center gap-3 rounded-[14px] bg-dark-surface p-3">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-peach/40 to-purple-accent/40">
              <Tag className="size-7 text-peach" strokeWidth={2} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="text-base font-semibold text-purple-light">85% Discount Activated</p>
              <p className="line-clamp-2 text-xs text-purple-light/70">
                Your Astro Map is now discounted.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
